import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "react-hot-toast";
import { QRCodeSVG } from "qrcode.react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Helper to get current date in GMT+7
const getCurrentDateGMT7 = () => {
  const now = new Date();
  // Add 7 hours to get GMT+7
  const gmt7 = new Date(now.getTime() + (7 * 60 * 60 * 1000));
  return gmt7;
};

// Helper to format date as YYYY-MM-DD in GMT+7
const formatDateKey = (dateStr) => {
  const date = new Date(dateStr);
  const gmt7 = new Date(date.getTime() + (7 * 60 * 60 * 1000));
  return gmt7.toISOString().split('T')[0];
};

// Helper to format date for display
const formatDateDisplay = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('id-ID', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

const TicketsPage = () => {
  const navigate = useNavigate();
  const { token, user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('buy'); // buy, history
  const [sessions, setSessions] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');

  const fetchSessions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tickets/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data.data || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Gagal memuat sesi tiket');
    }
  }, [token]);

  const fetchMyTickets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tickets/my-tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyTickets(response.data.data || []);
    } catch (error) {
      console.error('Error fetching my tickets:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSessions(), fetchMyTickets()]);
      setLoading(false);
    };
    if (token) {
      loadData();
    }
  }, [token, fetchSessions, fetchMyTickets]);

  // Group sessions by date for dropdown
  const availableDates = useMemo(() => {
    const now = getCurrentDateGMT7();
    const todayStr = now.toISOString().split('T')[0];
    
    // Filter sessions with date >= today and group by date
    const dateMap = new Map();
    
    sessions.forEach(session => {
      const sessionDateKey = formatDateKey(session.tanggal_pertandingan);
      
      // Only include dates >= today
      if (sessionDateKey >= todayStr) {
        if (!dateMap.has(sessionDateKey)) {
          dateMap.set(sessionDateKey, {
            dateKey: sessionDateKey,
            displayDate: formatDateDisplay(session.tanggal_pertandingan),
            sessions: []
          });
        }
        dateMap.get(sessionDateKey).sessions.push(session);
      }
    });
    
    // Sort by date
    return Array.from(dateMap.values()).sort((a, b) => a.dateKey.localeCompare(b.dateKey));
  }, [sessions]);

  // Get sessions for selected date
  const filteredSessions = useMemo(() => {
    if (!selectedDate) return [];
    const dateData = availableDates.find(d => d.dateKey === selectedDate);
    return dateData ? dateData.sessions : [];
  }, [selectedDate, availableDates]);

  // Auto-select first available date when data loads
  useEffect(() => {
    if (availableDates.length > 0 && !selectedDate) {
      setSelectedDate(availableDates[0].dateKey);
    }
  }, [availableDates, selectedDate]);

  const handlePurchase = async (session) => {
    if (purchasing) return;
    
    const userBalance = user?.tokens_balance || 0;
    if (userBalance < session.harga_token) {
      toast.error(`Saldo token tidak cukup. Dibutuhkan ${session.harga_token} token.`);
      return;
    }
    
    if (!window.confirm(`Beli tiket "${session.nama_sesi}" dengan ${session.harga_token} token?`)) {
      return;
    }
    
    setPurchasing(session.id);
    try {
      const response = await axios.post(
        `${API}/tickets/purchase`,
        { session_id: session.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        <div>
          <div className="font-bold">Tiket Berhasil Dibeli!</div>
          <div className="text-sm">Kode: {response.data.kode_tiket}</div>
        </div>
      );
      
      fetchSessions();
      fetchMyTickets();
      if (refreshUser) refreshUser();
      setActiveTab('history');
      
    } catch (error) {
      console.error('Error purchasing ticket:', error);
      toast.error(error.response?.data?.detail || 'Gagal membeli tiket');
    } finally {
      setPurchasing(null);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('id-ID', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('id-ID', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold">
            Aktif
          </span>
        );
      case 'redeemed':
        return (
          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold">
            Sudah Ditukar
          </span>
        );
      case 'cancelled':
        return (
          <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-bold">
            Dibatalkan
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-bold">
            {status}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat tiket...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-ltb-br relative pb-20">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_bc6efd8f-02f7-4fdc-a397-f7bf259d1836/artifacts/uov20vz1_Pattern%20LTB.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}></div>
      
      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="bg-white shadow-md py-3 sm:py-4 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto flex justify-between items-center">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">Kembali</span>
            </button>
            <h1 className="text-lg sm:text-xl font-bold text-gray-800">Tiket Pertandingan</h1>
            <div className="flex items-center gap-2">
              <div className="bg-blue-500 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                {user?.tokens_balance || 0} Token
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-6xl mx-auto px-4 py-6">
          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-lg mb-6 p-1 flex">
            <button
              onClick={() => setActiveTab('buy')}
              data-testid="tab-buy-ticket"
              className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                activeTab === 'buy'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
                Beli Tiket
              </span>
            </button>
            <button
              onClick={() => setActiveTab('history')}
              data-testid="tab-ticket-history"
              className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                activeTab === 'history'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Riwayat ({myTickets.length})
              </span>
            </button>
          </div>

          {/* Buy Ticket Tab */}
          {activeTab === 'buy' && (
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Pilih Jadwal Pertandingan</h2>
              
              {availableDates.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-20 h-20 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-500">Belum ada jadwal pertandingan tersedia</p>
                  <p className="text-sm text-gray-400 mt-2">Silakan cek kembali nanti</p>
                </div>
              ) : (
                <>
                  {/* Date Selector Dropdown */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pilih Tanggal
                    </label>
                    <select
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800 font-medium"
                      data-testid="date-selector"
                    >
                      {availableDates.map((dateInfo) => (
                        <option key={dateInfo.dateKey} value={dateInfo.dateKey}>
                          {dateInfo.displayDate} ({dateInfo.sessions.length} sesi)
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Sessions for Selected Date */}
                  {filteredSessions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>Pilih tanggal untuk melihat sesi yang tersedia</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <h3 className="font-semibold text-gray-700">
                        Sesi pada {availableDates.find(d => d.dateKey === selectedDate)?.displayDate}
                      </h3>
                      {filteredSessions.map((session) => {
                        const availableQuota = session.kuota - session.kuota_terpakai;
                        const isSoldOut = availableQuota <= 0;
                        
                        return (
                          <div
                            key={session.id}
                            data-testid={`session-card-${session.id}`}
                            className={`border-2 rounded-xl p-4 transition-all ${
                              isSoldOut 
                                ? 'border-gray-200 bg-gray-50 opacity-60' 
                                : 'border-blue-200 hover:border-blue-400 hover:shadow-md'
                            }`}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-lg font-bold text-sm">
                                    {formatTime(session.tanggal_pertandingan)}
                                  </div>
                                  <h3 className="font-bold text-gray-900 text-lg">{session.nama_sesi}</h3>
                                </div>
                                {session.deskripsi && (
                                  <p className="text-sm text-gray-600 mt-2">{session.deskripsi}</p>
                                )}
                                {session.lokasi && (
                                  <div className="flex items-center gap-1 mt-2 text-sm text-gray-600">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    {session.lokasi}
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex flex-col items-end gap-2">
                                <div className="text-right">
                                  <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full font-bold">
                                    {session.harga_token} Token
                                  </span>
                                </div>
                                <div className={`text-sm ${isSoldOut ? 'text-red-600' : 'text-green-600'} font-medium`}>
                                  {isSoldOut ? 'SOLD OUT' : `Tersisa ${availableQuota} tiket`}
                                </div>
                                <button
                                  onClick={() => handlePurchase(session)}
                                  disabled={isSoldOut || purchasing === session.id}
                                  data-testid={`buy-btn-${session.id}`}
                                  className={`px-6 py-2 rounded-lg font-bold transition-all ${
                                    isSoldOut
                                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                                  } disabled:opacity-50`}
                                >
                                  {purchasing === session.id ? (
                                    <span className="flex items-center gap-2">
                                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                      Membeli...
                                    </span>
                                  ) : isSoldOut ? (
                                    'Habis'
                                  ) : (
                                    'Beli Tiket'
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* History Tab - Riwayat Pembelian */}
          {activeTab === 'history' && (
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Riwayat Pembelian Tiket</h2>
              
              {myTickets.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-20 h-20 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <p className="text-gray-500">Belum ada riwayat pembelian tiket</p>
                  <button
                    onClick={() => setActiveTab('buy')}
                    className="mt-4 bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold"
                  >
                    Beli Tiket
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {myTickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      data-testid={`ticket-card-${ticket.id}`}
                      className="border-2 border-gray-200 rounded-xl p-4 hover:border-blue-300 cursor-pointer transition-all"
                      onClick={() => setSelectedTicket(ticket)}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-start gap-4">
                          {/* Mini QR Code */}
                          <div className="bg-white p-2 rounded-lg border shadow-sm flex-shrink-0">
                            <QRCodeSVG 
                              value={ticket.kode_tiket}
                              size={60}
                              level="M"
                            />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono font-bold text-blue-600 text-lg">{ticket.kode_tiket}</span>
                              {getStatusBadge(ticket.status)}
                            </div>
                            <h3 className="font-semibold text-gray-900">{ticket.m_ticket_session?.nama_sesi || '-'}</h3>
                            <div className="text-sm text-gray-600 mt-1">
                              {formatShortDate(ticket.m_ticket_session?.tanggal_pertandingan)}
                              {ticket.m_ticket_session?.lokasi && ` • ${ticket.m_ticket_session.lokasi}`}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">Tap untuk detail</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-6">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-medium text-blue-800">Cara Membeli & Menukar Tiket</p>
                <ul className="text-sm text-blue-700 mt-1 list-disc list-inside">
                  <li>Pilih tanggal pertandingan dari dropdown</li>
                  <li>Pilih sesi dan klik "Beli Tiket"</li>
                  <li>Tunjukkan QR Code tiket saat penukaran tiket fisik</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ticket Detail Modal with QR Code */}
      {selectedTicket && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedTicket(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Ticket Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 text-center">
              <div className="text-sm opacity-80 mb-1">KODE TIKET</div>
              <div className="text-3xl font-mono font-bold tracking-wider">{selectedTicket.kode_tiket}</div>
              <div className="mt-2">{getStatusBadge(selectedTicket.status)}</div>
            </div>

            {/* QR Code Section */}
            <div className="bg-gray-50 p-6 flex justify-center">
              <div className="bg-white p-4 rounded-xl shadow-lg">
                <QRCodeSVG 
                  value={selectedTicket.kode_tiket}
                  size={180}
                  level="H"
                  includeMargin={true}
                />
              </div>
            </div>
            
            {/* Ticket Body */}
            <div className="p-6">
              <div className="border-b pb-4 mb-4">
                <h3 className="font-bold text-xl text-gray-900">{selectedTicket.m_ticket_session?.nama_sesi}</h3>
                {selectedTicket.m_ticket_session?.deskripsi && (
                  <p className="text-gray-600 text-sm mt-1">{selectedTicket.m_ticket_session.deskripsi}</p>
                )}
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <div>
                    <div className="text-xs text-gray-500">Tanggal Pertandingan</div>
                    <div className="font-medium">{formatDateTime(selectedTicket.m_ticket_session?.tanggal_pertandingan)}</div>
                  </div>
                </div>
                
                {selectedTicket.m_ticket_session?.lokasi && (
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <div className="text-xs text-gray-500">Lokasi</div>
                      <div className="font-medium">{selectedTicket.m_ticket_session.lokasi}</div>
                    </div>
                  </div>
                )}

                {selectedTicket.status === 'redeemed' && selectedTicket.redeemed_at && (
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <div className="text-xs text-gray-500">Waktu Penukaran</div>
                      <div className="font-medium text-green-600">{formatDateTime(selectedTicket.redeemed_at)}</div>
                    </div>
                  </div>
                )}
              </div>
              
              {selectedTicket.status === 'active' && (
                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 text-center">
                    <span className="font-bold">Tunjukkan QR Code ini</span> saat penukaran tiket fisik di lokasi pertandingan
                  </p>
                </div>
              )}
            </div>
            
            {/* Close Button */}
            <div className="px-6 pb-6">
              <button
                onClick={() => setSelectedTicket(null)}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 rounded-xl font-bold"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketsPage;
